import { useEffect, useState } from 'react';
import useIntl from '../intl/useIntl';
import usePrevious from '../component/usePrevious';
import isEqual from 'lodash.isequal';
import camelize from '@afosto/utils/string/camelize';
import capitalize from '@afosto/utils/string/capitalize';
import sortByArray from '@afosto/utils/array/sortByArray';
import { index as search } from '@afosto/content/api/Index';
import { profile as profileApi } from '@afosto/iam/api/Profile';

const DEFAULT_DATA_SOURCE_PROFILE_OPTIONS = {
  namespace: 'default',
  profileNamespace: 'afosto',
  profileVersion: 'v1',
  filterTypes: {},
  ignoreFilters: ['indexed_at', 'reference'],
  ignoreProperties: [],
  ignoreSorting: ['boolean'],
  propertyTypes: {},
  shouldFetchDefinitions: true,
};

const DEFAULT_DATA_SOURCE_PROFILE = {
  filters: [],
  properties: [],
  views: [],
};

export default (options = {}) => {
  const intl = useIntl();
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const [profile, setProfile] = useState(null);
  const previousOptions = usePrevious(options);

  useEffect(() => {
    const {
      code,
      model,
      namespace,
      filterTypes,
      ignoreFilters,
      ignoreProperties,
      ignoreSorting,
      propertyTypes,
      profileNamespace,
      profilePath = `${code}.${model}`,
      profileVersion,
      shouldFetchDefinitions,
    } = { ...DEFAULT_DATA_SOURCE_PROFILE_OPTIONS, ...options };

    const fetchDefinitions = async () => {
      try {
        const response = await search.listDefinitions({ namespace, code, model });
        const definitions = response?.data ?? [];
        const filters = definitions
          .reduce((acc, definition) => [...acc, ...(definition.filters ?? [])], [])
          .filter(({ key }) => !ignoreFilters.includes(key))
          .map(({ key, type }) => {
            const filterName = capitalize(key.replace('_', ' '));

            return {
              key,
              name: intl.formatMessage({
                id: `dataGrid.columns.${camelize(model)}.${key}`,
                defaultMessage: filterName,
                description: `The name for filter ${filterName}`,
              }),
              type: filterTypes[key] ?? type,
            };
          });

        const properties = filters
          .filter(({ key }) => !ignoreProperties.includes(key))
          .reduce((acc, { key, name, type }) => {
            const isSortable = !ignoreSorting.includes(type);
            return [...acc, { key, name, type: propertyTypes[key] ?? type, isSortable }];
          }, []);

        return { filters, properties };
      } catch (err) {
        return { filters: [], properties: [] };
      }
    };

    const fetchProfile = async scope => {
      try {
        const response = await profileApi.getProfile(
          `${profileNamespace}.${scope}.${profilePath}.${profileVersion}`,
        );
        const positions = response?.data?.positions ?? [];
        const viewsData = response?.data?.views ?? {};
        const views = Object.keys(viewsData).reduce((acc, viewKey) => {
          const view = viewsData[viewKey] ?? {};
          let label = '';

          if (scope === 'me') {
            label = `(${intl.formatMessage({
              id: 'dataGrid.views.label.private',
              defaultMessage: 'Private',
              description: 'Data grid private views label',
            })})`;
          }

          const properties = (view.selection ?? []).reduce((propertyAcc, property) => {
            const { key, heading, format } = property;
            const isSortable = !ignoreSorting.includes(format);

            return [...propertyAcc, { key, name: heading, type: format, isSortable }];
          }, []);

          const filtersMappedByKey = (view.filters ?? []).reduce((filtersAcc, filter) => {
            if (filtersAcc[filter.key]) {
              return { ...filtersAcc, [filter.key]: [...filtersAcc[filter.key], filter] };
            }

            return { ...filtersAcc, [filter.key]: [filter] };
          }, {});
          const filters = Object.keys(filtersMappedByKey).reduce((filtersAcc, filterKey) => {
            const mappedFilters = filtersMappedByKey[filterKey] ?? [];

            if (mappedFilters.length > 1) {
              const [firstFilter, secondFilter] = filters;

              return [
                ...filtersAcc,
                {
                  key: filterKey,
                  operator: 'between',
                  value: [firstFilter.value, secondFilter.value],
                },
              ];
            }

            return [...filtersAcc, ...mappedFilters];
          }, []);

          return [
            ...acc,
            {
              key: `${scope}.${viewKey}`,
              type: scope === 'me' ? 'user' : 'tenant',
              name: view.name,
              label: `${view.name} ${label}`.trim(),
              description: view.description,
              itemsPerPage: view.pagesize,
              customProperties: [],
              properties,
              filters,
              sort: view.sort ?? [],
              active: view.active ?? true,
              isDefault: view.is_default,
            },
          ];
        }, []);

        return { scope, views, positions };
      } catch (err) {
        return { scope, views: [], positions: [] };
      }
    };

    const fetchProfiles = async () => {
      try {
        const promises = await Promise.allSettled([fetchProfile('tenant'), fetchProfile('me')]);
        const profiles = promises
          .filter(promise => promise.status === 'fulfilled')
          .map(promise => promise.value);
        const privateProfile = profiles.find(({ scope }) => scope === 'me');
        const positions = privateProfile?.positions ?? [];
        const views = profiles.reduce((acc, result) => [...acc, ...(result?.views ?? [])], []);
        const sortedViews = sortByArray(views, positions, 'key');

        return { views: sortedViews };
      } catch (err) {
        return { views: [] };
      }
    };

    const fetchDataSourceProfile = async () => {
      try {
        setIsFetchingProfile(true);
        const promises = await Promise.allSettled([
          fetchProfiles(),
          ...(shouldFetchDefinitions ? [fetchDefinitions()] : []),
        ]);
        const result = promises
          .filter(promise => promise.status === 'fulfilled')
          .reduce((acc, response) => ({ ...acc, ...(response?.value ?? {}) }), {});

        setProfile(result);
      } catch (err) {
        setProfile(DEFAULT_DATA_SOURCE_PROFILE);
      } finally {
        setIsFetchingProfile(false);
      }
    };

    if (!isEqual(options, previousOptions)) {
      fetchDataSourceProfile();
    }
  }, [intl, options, previousOptions]);

  return { isFetchingProfile, ...(profile ?? DEFAULT_DATA_SOURCE_PROFILE) };
};
